import React from 'react';
import { getAuth, GoogleAuthProvider, signInWithPopup, UserCredential } from 'firebase/auth';

// Google's branding guidelines (https://developers.google.com/identity/branding-guidelines)
// require the "G" mark to be reproduced unmodified, at its official colors, on any
// "Sign in with Google" button. Kept as an inline SVG (no network fetch, themeable-safe).
const GoogleLogo: React.FC = () => (
  <svg width='18' height='18' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg' aria-hidden='true' focusable='false'>
    <path fill='#4285F4' d='M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615Z' />
    <path fill='#34A853' d='M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A9 9 0 0 0 9 18Z' />
    <path fill='#FBBC05' d='M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A9 9 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z' />
    <path fill='#EA4335' d='M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A9 9 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58Z' />
  </svg>
);

const GoogleSignIn: React.FC = () => {
  // Initialize Firebase Auth and Google Provider
  const auth = getAuth();
  const provider = new GoogleAuthProvider();

  // Same theme signal Login.tsx's Google Identity Services button reads to pick its
  // 'outline' (light) vs 'filled_black' (dark) variant — kept consistent here so this
  // button (used on the standalone /auth/google-login route) matches in both modes.
  const isDarkMode = typeof window !== 'undefined'
    && localStorage.getItem('kt_theme_mode_value') === 'dark';

  const signInWithGoogle = async () => {
    try {
      // Open the Google sign-in popup
      const response: UserCredential = await signInWithPopup(auth, provider);

      // If successful, retrieve the user token
      if (response && response.user) {
        await response.user.getIdToken();
        // TODO: send the token to the backend — this route isn't wired into any
        // real sign-in flow yet (see comment below); left as-is, pre-existing behavior.
      }
    } catch (error) {
      console.error('Error during Google sign-in:', error);
    }
  };

  // Colors/sizing follow Google's official "Sign in with Google" button spec: 40px height,
  // Roboto 500/14px label text, and the prescribed light (white bg, #747775 border, #1F1F1F
  // text) / dark (#131314 bg, #8E918F border, #E3E3E3 text) color pairs.
  const buttonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    height: 40,
    padding: '0 12px',
    borderRadius: 4,
    border: `1px solid ${isDarkMode ? '#8E918F' : '#747775'}`,
    backgroundColor: isDarkMode ? '#131314' : '#FFFFFF',
    color: isDarkMode ? '#E3E3E3' : '#1F1F1F',
    fontFamily: "'Roboto', Arial, sans-serif",
    fontWeight: 500,
    fontSize: 14,
    letterSpacing: '0.25px',
    cursor: 'pointer',
  };

  return (
    <div>
      <button
        type='button'
        onClick={signInWithGoogle}
        style={buttonStyle}
        aria-label={'Sign in with Google'.toTranslatedDisplay()}
      >
        <GoogleLogo />
        <span>{'Sign in with Google'.toTranslatedDisplay()}</span>
      </button>
    </div>
  );
};

export default GoogleSignIn;
